import {
    IAnalyzerColorNumber,
    IAnalyzerElementBack,
    IAnalyzerTemplateOptions,
    IAnalyzerTemplateProps, IAutoTag, ITemplateAnalyzerResult,
    ITemplateContentAnalyzerService,
    ITemplateContentAnalyzerServiceProps,
    IUserAnalyzerResult,
    IVerifiedContentsTitle,
    IVerifiedCoverTitle,
    NonStandardPageTypeIndex,
    PageTypeIndex,
    TContentsNumberType,
} from '@src/slide/application/service/template-content-analyzer-service/index.interface';
import { STSchemeColorVal } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import {
    PageText,
    PageType,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/page-content';
import {
    TextLeafNode,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/text-leaf-node';
import { IRect } from '@src/slide/application/service/change-template-service/index.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IHSL, ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { transaction } from '@src/slide/domain/slide-app';
import { IPresentationData } from '@tencent/slide-sdk-model';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import PresentationModel from '@src/slide/domain/ui-model/element-modeles/presentation-model';
import { UiDataService } from '@src/slide/domain/ui-model/service/ui-data-service';
import { fetchTemplateByIdByNonVip } from '@src/slide/application/service/ai-ssistant/ai-generator/fetch-template';
import { convert } from '@tencent/slide-markdown-converter';
import cloneDeep from 'lodash/cloneDeep';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { IConnectorModel } from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';

interface ITitleCached {
    text: string;
    isMapped: boolean;
}

enum EIndexType {
    number,
    simple,
    tradition,
    english,
}

// 判断两个元素是否相似时，给一个尺寸判断的buffer
const MIN_SCALE = 0.8;
const MAX_SCALE = 1.2;

/**
 * 文档、（非）标准模版分析
 */
export class TemplateContentAnalyzerService implements ITemplateContentAnalyzerService {
    private service: ITemplateContentAnalyzerServiceProps;
    private contentsTitleCached: ITitleCached[] = [];
    private sectionCached = new Map<string, {
        model: PageText;
        type: EIndexType;
    }>();

    private firstSectionIndex = '';

    public setDelegateService(service: ITemplateContentAnalyzerServiceProps) {
        this.service = service;
    }

    public clearCached() {
        this.contentsTitleCached = [];
        this.sectionCached = new Map<string, {model: PageText; type: EIndexType}>();
        this.firstSectionIndex = '';
    }

    /**
     * 分析非标准正文背景、主要文字色
     */
    /* eslint-disable complexity */
    public analyzerContentBackground(pageTexts: PageText[], changeBackground = true): {
        slideBack: IAnalyzerElementBack;
        mainFontColor: ITDColor | undefined;
    } {
        this.clearCached();
        // const { templatePresentation } = props;
        // const templatePageTextList = await this.service.contentLayoutAnalyzer
        // .analysisTextResult(templatePresentation);

        const slideBack: IAnalyzerElementBack = {
            fillBackground: undefined,
            firstElement: undefined,
            secondElement: undefined,
            thirdElement: undefined,
            pageModel: pageTexts[0].pageModel,
            relationLayoutIndex: undefined,
        };

        // 出现最多的文字颜色
        let mainFontColor: ITDColor | undefined;
        const colorMap: {
            [colorName: string]: {
                number: number;
                tdColor: ITDColor;
                isUpperLum: boolean
            };
        } = {};
        let colorLowLumMaxNumber = 0;
        let colorUpperLumNumber = 0;


        const presentationModel = pageTexts[0]?.pageModel?.parent;
        if (!presentationModel) return {
            slideBack,
            mainFontColor: undefined,
        };

        const firstSlide = pageTexts[0].pageModel;
        const layoutModel = firstSlide.getRelationLayoutModel();
        if (firstSlide.parent?.type === UIModelType.PRESENTATION) {
            const presentationModel = firstSlide.parent as IPresentationModel;
            slideBack.relationLayoutIndex = presentationModel.layoutList.findIndex(layout => layoutModel === layout);
        }

        pageTexts.forEach((page, index) => {
            if (index > 3 || !page) return;
            const { pageModel, texts } = page;
            const elementRectMap = new Map<IElementBaseModel, {
                rect: {
                    left: number,
                    top: number,
                    width: number,
                    height: number,
                },
                fillColor: ITDColor,
                element: IElementBaseModel,
            }>();
            texts?.forEach((text) => {
                const element = text.contentBlock?.visualBlocks?.[0]?.info?.element;
                if (element) {
                    const { paragraphList, props } = element as IShapeModel;

                    paragraphList?.forEach((para) => {
                        const { textRuns } = para;
                        textRuns?.forEach((text) => {
                            if (text.styles) {
                                const { tdColor } = text.styles;
                                if (tdColor && this.hasFillLowerFill(element, elementRectMap, tdColor)) {
                                    return;
                                }

                                if (tdColor) {
                                    const isUpperLum = tdColor.hsl.L > 120;
                                    if (colorMap[tdColor.srgb_color]) {
                                        colorMap[tdColor.srgb_color].number = colorMap[tdColor.srgb_color].number + 1;
                                    } else {
                                        colorMap[tdColor.srgb_color] = {
                                            number: 1,
                                            tdColor,
                                            isUpperLum,
                                        };
                                    }

                                    if (isUpperLum) {
                                        colorUpperLumNumber = colorUpperLumNumber + 1;
                                    } else {
                                        colorLowLumMaxNumber = colorLowLumMaxNumber + 1;
                                    }
                                }
                            }
                        });
                    });
                }
            });

            this.getSlideBack(slideBack, pageModel, index, presentationModel as IPresentationModel);
        });

        let maxColorNumber = 0;
        Object.keys(colorMap).filter((key) => {
            const colorItem = colorMap[key];
            return colorItem.isUpperLum === (colorUpperLumNumber > colorLowLumMaxNumber);
        })
            .forEach((key) => {
                const colorItem = colorMap[key];
                if (colorItem.number > maxColorNumber) {
                    maxColorNumber = colorItem.number;
                    mainFontColor = colorItem.tdColor;
                }
            });

        // 如果是slide类背景，就将背景插入到layout中
        if (changeBackground) {
            const { firstElement } = slideBack;
            if (firstElement) {
                transaction(() => {
                    layoutModel.addElementWithNoSelect([firstElement.type], [firstElement.props]);
                });
            }

            const { secondElement } = slideBack;
            if (secondElement) {
                transaction(() => {
                    layoutModel.addElementWithNoSelect([secondElement.type], [secondElement.props]);
                });
            }

            const { thirdElement } = slideBack;
            if (thirdElement) {
                transaction(() => {
                    layoutModel.addElementWithNoSelect([thirdElement.type], [thirdElement.props]);
                });
            }
        }

        pageTexts.forEach((page, index) => {
            if (index > 3 || !page) return;
            const { pageModel } = page;
            if (!slideBack.firstElement) {
                const layoutModel = pageModel.getRelationLayoutModel();
                this.getSlideBack(slideBack, layoutModel, index, presentationModel as IPresentationModel);
            }
        });

        pageTexts.forEach((page, index) => {
            if (index > 3 || !page) return;
            const { pageModel } = page;
            if (!slideBack.firstElement) {
                const masterModel = pageModel.getRelationMasterModel();
                this.getSlideBack(slideBack, masterModel, index, presentationModel as IPresentationModel);
            }
        });

        return {
            slideBack,
            mainFontColor,
        };
    }

    public checkSimilarHsl = (templateHsl: IHSL, mainFontHsl: IHSL): boolean => {
        const diffL = Math.abs(templateHsl.L - mainFontHsl.L);

        if (diffL < 20) {
            return true;
        } if (diffL < 50) {
            const diffH =  Math.abs(templateHsl.H - mainFontHsl.H);
            if (diffH < 30) {
                return true;
            }

            if (Math.abs(diffH - 255) < 30) {
                return true;
            }
        }

        return false;
    };


    public checkRectOverLap = (rect: IRect, newRect: IRect) => {
        const x1 = rect.left;
        const x2 = rect.left + rect.width;
        const y1 = rect.top;
        const y2 = rect.top + rect.height;

        const x3 = newRect.left;
        const x4 = newRect.left + newRect.width;
        const y3 = newRect.top;
        const y4 = newRect.top + newRect.height;

        if (y1 > y4) return false;
        if (x2 < x3) return false;
        if (y2 < y3) return false;
        if (x1 > x4) return false;

        return true;
    };


    public hasFillLowerFill = (element: IElementBaseModel, elementRectMap: Map<IElementBaseModel, {
        rect: IRect,
        fillColor: ITDColor,
        element: IElementBaseModel,
    }>, mainFontColor: ITDColor): boolean => {
        let flag = false;
        const newRect = this.getRect(element);
        const fillTDColor = this.getFillColor(element);
        elementRectMap.forEach((value, key) => {
            const { rect, fillColor, element } = value;
            if (this.checkRectOverLap(rect, newRect)) {
                if (element.props.style.fillBackground?.solidFill
                    || element.props.style.fillBackground?.gradFill
                    || element.props.style.fillBackground?.image
                    || element.props.style.fillBackground?.pattFill
                ) {
                    flag = true;
                }
            }
        });

        if (newRect && fillTDColor) {
            elementRectMap.set(element, {
                rect: newRect,
                fillColor: fillTDColor,
                element,
            });
        }

        return flag;
    };

    public getFillColor = (element: IElementBaseModel): ITDColor | null | undefined => {
        const { fillBackground } = element.props.style;
        return fillBackground?.solidFill?.color;
    };

    public getRect = (element: IElementBaseModel): IRect => {
        const { props } = element;
        const { style } = props;
        return {
            left: style.left,
            top: style.top,
            width: style.width,
            height: style.height,
        };
    };

    /**
     * 分析背景类信息，包括背景类元素、背景填充
     * @param slideBack
     * @param pageModel
     * @param index
     * @param presentation
     */
    public getSlideBack(
        slideBack: IAnalyzerElementBack,
        pageModel: ISlideModel,
        index: number,
        presentation: IPresentationModel,
    ) {
        // 如果有背景，记录下背景属性
        if (!slideBack.fillBackground) {
            if (pageModel.props.style.fillBackground?.noFill !== 1) {
                slideBack.fillBackground = pageModel.props.style.fillBackground;
            }
        } else if (slideBack.fillBackground?.color !== pageModel.props.style.fillBackground?.color) {
            slideBack.fillBackground = undefined;
        }

        // 记录最下面三个元素，从第四页开始，暂定分析三页
        const firstElement = pageModel.children[0];
        if (!firstElement || !this.isBackgroundElement({
            element: firstElement,
            slide: pageModel,
            isStrict: false,
            presentation,
        })) {
            slideBack.firstElement = undefined;
        } else if (!slideBack.firstElement) {
            if (index === 0) {
                slideBack.firstElement = firstElement;
            }
        } else {
            if (!this.compareSameElement(slideBack.firstElement, firstElement)) {
                slideBack.firstElement = undefined;
            }
        }


        const secondElement = pageModel.children[1];
        if (!secondElement || !this.isBackgroundElement({
            element: secondElement,
            slide: pageModel,
            isStrict: false,
            presentation,
        })) {
            slideBack.secondElement = undefined;
        } else if (!slideBack.secondElement) {
            if (index === 0) {
                slideBack.secondElement = secondElement;
            }
        } else {
            if (!this.compareSameElement(slideBack.secondElement, secondElement)) {
                slideBack.secondElement = undefined;
            }
        }


        const thirdElement = pageModel.children[2];
        if (!thirdElement || !this.isBackgroundElement({
            element: thirdElement,
            slide: pageModel,
            isStrict: false,
            presentation,
        })) {
            slideBack.thirdElement = undefined;
        } else if (!slideBack.thirdElement) {
            if (index === 0) {
                slideBack.thirdElement = thirdElement;
            }
        } else {
            if (!this.compareSameElement(slideBack.thirdElement, thirdElement)) {
                slideBack.thirdElement = undefined;
            }
        }
    }

    /**
     * 是否可以认定为一个背景元素
     * 比较大、没文字
     */
    public isBackgroundElement(props: {
        element?: IElementBaseModel,
        slide: ISlideModel,
        isStrict: boolean,
        presentation: IPresentationModel,
    }) {
        const {
            element,
            slide,
            isStrict,
            presentation,
        } = props;
        if (!element) return false;

        if (element.type === UIModelType.GROUP) {
            return false;
        }

        let flag = true;
        const { slideWidth, slideHeight } = presentation.props;
        const { width = 0, height = 0 } = element.props.style || {};
        const scale = isStrict ? 0.8 : 0.02;

        if (element.type === UIModelType.IMAGE) {
            if (width * height > (slideWidth * slideHeight * scale)) {
                return true;
            }
        }

        if (element.type === UIModelType.SHAPE) {
            const shapeElement = element as IShapeModel;
            if (width * height > (slideWidth * slideHeight * scale)) {
                const {
                    paragraphList,
                } = shapeElement;
                paragraphList?.forEach((para) => {
                    const { textRuns } = para;
                    textRuns?.forEach((textRun) => {
                        const { text } = textRun;
                        if (text) {
                            flag = false;
                        }
                    });
                });

                return flag;
            }

            return false;
        }

        return false;
    }

    /**
     * 对比两个元素是否是同一个元素
     */
    public compareSameElement(oldElement: IElementBaseModel, newElement: IElementBaseModel) {
        const { width: oldWidth, height: oldHeight } = oldElement.props.style || {};
        const { width: newWidth, height: newHeight } = newElement.props.style || {};
        const { type: oldType } = oldElement || {};
        const { type: newType } = newElement || {};
        const oldColor = oldElement.props.style.fillBackground?.color;
        const newColor = newElement.props.style.fillBackground?.color;
        const oldChildNumber = oldElement.children.length;
        const newChildNumber = newElement.children.length;
        return (oldWidth > newWidth * 0.9 && oldWidth < newWidth * 1.1)
            && (oldHeight > newHeight * 0.9 && oldHeight < newHeight * 1.1)
            && oldColor === newColor
            && oldType === newType
            && oldChildNumber === newChildNumber;
    }

    /**
     * 分析元素类背景，类似与用一张大图或者多个无填充元素做背景色
     */
    public analyzerElementBackground(slideModelList: ISlideModel[]) {
        slideModelList.forEach((slideModel, slideIndex) => {
            const { children } = slideModel;
            children.forEach((element, elementIndex) => {
                if (elementIndex >= 3) return;

                if (element.isPlaceholder) return;
            });
        });
    }

    /**
     * 分析用户数据
     */
    public async analyzerUserDocs(props: IAnalyzerTemplateProps): Promise<IUserAnalyzerResult> {
        this.clearCached();
        const { templatePresentation } = props;
        const templatePageTextList = await this.service.contentLayoutAnalyzer.analysisTextResult(templatePresentation);

        let userDocCoverResult: IVerifiedCoverTitle | undefined;
        let userDocContentsResult: IVerifiedContentsTitle | undefined;
        const userDocSectionResult: IVerifiedCoverTitle[] = [];
        let userDocBackCoverResult: IVerifiedCoverTitle | undefined;
        const userDocContentResult: PageText[] = [];

        templatePageTextList.forEach((page, index) => {
            if (index === 0 && page) {
                page.pageType = PageType.CoverPage;
            }

            switch (page.pageType) {
                case PageType.CoverPage:
                    userDocCoverResult = this.analyzerTemplateCover(page);
                    break;
                case PageType.TableOfContents:
                    userDocContentsResult = this.analyzerTemplateContentsUnKnow(page);
                    if (userDocContentsResult) {
                        userDocContentsResult.titleLeafList?.forEach((leaf) => {
                            this.contentsTitleCached.push({
                                text: leaf.text,
                                isMapped: false,
                            });
                        });
                    }
                    break;
                case (PageType.Content || PageType.SectionDivider): {
                    const content = this.analyzerUserSection(page);
                    if (page.texts?.length <= 3) {
                        const titleCached = this.contentsTitleCached.find(this.getPredicate(content));
                        if (titleCached && !titleCached.isMapped) {
                            // 有目录，且在目录里找到了对应的标题
                            userDocSectionResult.push(content);
                            page.pageType = PageType.SectionDivider;
                            titleCached.isMapped = true;
                        } else if (content.indexLeaf) {
                            // 无目录但是有索引
                            const { text } = content.indexLeaf;
                            const type = this.getIndexType(text);
                            const firstType = this.getIndexType(this.firstSectionIndex);
                            if (this.sectionCached.size === 0) {
                                this.firstSectionIndex = text;
                                this.sectionCached.set(text, {
                                    model: page,
                                    type,
                                });
                            } else if (type === firstType && text !== this.firstSectionIndex) {
                                this.sectionCached.set(text, {
                                    model: page,
                                    type,
                                });
                            }
                        } else {
                            userDocContentResult.push(page);
                        }
                    } else {
                        userDocContentResult.push(page);
                    }
                    break;
                }
                case PageType.Acknowledgments:
                    userDocBackCoverResult = this.analyzerTemplateBackCover(page);
            }
        });

        if (userDocSectionResult.length === 0 && this.sectionCached.size > 0) {
            this.sectionCached.forEach((cached) => {
                const { model } = cached;
                model.pageType = PageType.SectionDivider;
                const content = this.analyzerUserSection(model);
                userDocSectionResult.push(content);
            });
        }

        return {
            userDocCoverResult,
            userDocContentsResult,
            userDocSectionResult,
            userDocBackCoverResult,
            userDocContentResult,
        };
    }

    /**
     * 获取序号首字，用来判断序号类型
     * @param indexText
     */
    public getIsFirst(indexText: string): boolean {
        indexText = indexText.toLowerCase()
            .replace('.', '')
            .replace(' ', '')
            .replace('no', '')
            .replace('章节', '')
            .replace('part', '');
        const regNumber = /0?1$/;
        const regNumberSimpleFont = /一/;
        const regNumberComplexFont = /壹/;
        const regNumberEnglish = /one/;
        const regNumberText = regNumber.exec(indexText)?.[0];
        const regSimpleFontText = regNumberSimpleFont.exec(indexText)?.[0];
        const regTraditionFontText = regNumberComplexFont.exec(indexText)?.[0];
        const regEnglishText = regNumberEnglish.exec(indexText)?.[0];

        return ((regNumberText?.length || 0) > 0)
            || (regSimpleFontText?.length || 0) > 0
            || (regTraditionFontText?.length || 0) > 0
            || (regEnglishText?.length || 0) > 0;
    }

    /**
     * 分析索引类别，用于正确的做索引自加1
     * @param indexText
     */
    public getIndexType(indexText: string): EIndexType {
        const regNumber = /0?[\d]$/;
        const regNumberSimpleFont = /一/;
        const regNumberComplexFont = /壹/;
        const regNumberEnglish = /one/;
        const regNumberText = regNumber.exec(indexText)?.[0];
        const regSimpleFontText = regNumberSimpleFont.exec(indexText)?.[0];
        const regTraditionFontText = regNumberComplexFont.exec(indexText)?.[0];
        const regEnglishText = regNumberEnglish.exec(indexText)?.[0];

        if ((regNumberText?.length || 0) > 0) {
            return EIndexType.number;
        }

        if ((regSimpleFontText?.length || 0) > 0) {
            return EIndexType.simple;
        }

        if ((regTraditionFontText?.length || 0) > 0) {
            return EIndexType.tradition;
        }

        if ((regEnglishText?.length || 0) > 0) {
            return EIndexType.english;
        }

        return EIndexType.number;
    }

    /**
     * 分析模版数据，模版数据需要遵守标准
     * @param props
     * @param options
     */
    public async analyzerTemplate(props: IAnalyzerTemplateProps, options: IAnalyzerTemplateOptions) {
        const { templatePresentation } = props;
        const templatePageTextList = await this.service.contentLayoutAnalyzer.analysisTextResult(templatePresentation);

        const coverPageText: PageText = templatePageTextList[PageTypeIndex.cover];
        const sectionPageText: PageText = templatePageTextList[PageTypeIndex.section];
        const backCoverPageText: PageText = templatePageTextList[templatePageTextList.length - 1];
        const templateCoverResult = this.analyzerTemplateCover(coverPageText);
        const templateContentsResult = this.analyzerTemplateContents(
            templatePageTextList[options.contentsNumber - 1],
            options.contentsNumber,
        );

        const templateSectionResult = this.analyzerTemplateSection(sectionPageText);
        const templateBackCoverResult = this.analyzerTemplateBackCover(backCoverPageText);
        const templateContentResult = [templatePageTextList[3], templatePageTextList[4], templatePageTextList[5]];
        const { slideBack, mainFontColor } = this.analyzerContentBackground(templateContentResult);
        return {
            templateCoverResult,
            templateContentsResult,
            templateSectionResult,
            templateBackCoverResult,
            templateContentBackResult: slideBack,
            mainFontColor,
        };
    }

    /**
     * 根据presentationData创建clone的uiDataService
     * @param presentationData
     */
    public createTemplateUiDataService = async (presentationData: IPresentationData): Promise<IUiDataService> => {
        const newPresentationStoreService: any = SlideDataServices.SDKDataServices.PresentationStoreService()
            ?.createFromData(presentationData);
        const newSdkPresentation = newPresentationStoreService?.presentation;
        const newPresentation = new PresentationModel({
        });

        const cloneUiDataService = new UiDataService();
        cloneUiDataService.presentation = newPresentation;
        cloneUiDataService.presentation.asyncConvertChildren = false;
        cloneUiDataService.presentation.asyncConvertSlideChildren = false;
        cloneUiDataService.setSlideRenderStateManagerDelegate(this.service.slideRenderStateManager);
        await newPresentation.bindSDK(newSdkPresentation);
        await cloneUiDataService?.forceConvertSlides(cloneUiDataService.presentation.children);
        return cloneUiDataService;
    };

    /**
     * 分析非标准模版数据，主要是包图网那些
     * @param props
     */
    public async analyzerNonStandardTemplate(props: IAnalyzerTemplateProps): Promise<ITemplateAnalyzerResult> {
        const { templatePresentation, textResult, changeBackground } = props;
        const { contentLayoutAnalyzer } = this.service;
        const templatePageTextList = textResult ?? await contentLayoutAnalyzer.analysisTextResult(templatePresentation);
        let hasSubCover = false;

        // 先分析第二页是不是目录页
        let contentsPage = templatePageTextList[NonStandardPageTypeIndex.fourContents];
        let templateContentsResult = this.analyzerTemplateContentsUnKnow(contentsPage);
        if (templateContentsResult.titleLeafList?.length) {
            templateContentsResult.titleLeafList?.forEach((leaf) => {
                this.contentsTitleCached.push({
                    text: leaf.text,
                    isMapped: false,
                });
            });
        } else {
            // 不是的话看看第三页是不是目录了页
            contentsPage = templatePageTextList[NonStandardPageTypeIndex.fourContents + 1];
            templateContentsResult = this.analyzerTemplateContentsUnKnow(contentsPage);
            if (templateContentsResult.titleLeafList?.length) {
                hasSubCover = true;
                templateContentsResult.titleLeafList?.forEach((leaf) => {
                    this.contentsTitleCached.push({
                        text: leaf.text,
                        isMapped: false,
                    });
                });
            }
        }

        // 如果有前言页，那所有相关推测的索引都要+1
        const indexOffset = hasSubCover ? 1 : 0;
        const coverPageText: PageText = templatePageTextList[NonStandardPageTypeIndex.cover];
        const sectionPageText: PageText = templatePageTextList[NonStandardPageTypeIndex.section + indexOffset];
        const backCoverPageText: PageText = templatePageTextList[templatePageTextList.length - 1];
        const templateCoverResult = this.analyzerTemplateCover(coverPageText);
        let templateSubCoverResult: IVerifiedCoverTitle | undefined;
        if (hasSubCover) {
            const subCoverPageText = templatePageTextList[indexOffset];
            templateSubCoverResult = this.analyzerTemplateSubCover(subCoverPageText);
        }

        const templateSectionResult = this.analyzerTemplateSection(sectionPageText);
        const templateBackCoverResult = this.analyzerTemplateBackCover(backCoverPageText);
        const firstContentIndex = 3 + indexOffset;
        const secondContentIndex = 4 + indexOffset;
        const thirdContentIndex = 5 + indexOffset;
        const templateContentResult = [
            templatePageTextList[firstContentIndex],
            templatePageTextList[secondContentIndex],
            templatePageTextList[thirdContentIndex],
        ];
        const { slideBack, mainFontColor } = this.analyzerContentBackground(templateContentResult, changeBackground);

        const noContentIndex = new Map<number, boolean>();
        templateSubCoverResult && noContentIndex.set(NonStandardPageTypeIndex.cover, true);
        templateSectionResult && noContentIndex.set(NonStandardPageTypeIndex.section + indexOffset, true);
        templateContentsResult && noContentIndex.set(NonStandardPageTypeIndex.fourContents + indexOffset, true);
        templateBackCoverResult && noContentIndex.set(templatePageTextList.length - 1, true);

        // 首个章节页目前是靠索引推测的，后续章节页通过找和首个章节页相似、且主标题出现在目录来判断
        const followingSection = this.getSimilarSection(
            templateContentsResult,
            templateSectionResult,
            templatePageTextList,
        );

        this.analyzerContent(templatePageTextList[3]);

        return {
            templateCoverResult,
            templateContentsResult,
            templateSectionResult,
            templateBackCoverResult,
            templateContentBackResult: slideBack,
            mainFontColor,
            templateSubCoverResult,
            followingSection,
        };
    }

    /**
     * 根据首个章节页，查找与它类似的其他章节页
     * @param templateContentsResult 目录，目录标题数量理论上应该对应章节页数量
     * @param templateSectionResult
     * @param templatePageTextList
     */
    public getSimilarSection(
        templateContentsResult: IVerifiedContentsTitle,
        templateSectionResult: IVerifiedCoverTitle,
        templatePageTextList: PageText[],
    ): IVerifiedCoverTitle[] {
        const subSimilarResult: IVerifiedCoverTitle[] = [];
        const contentsNumber = templateContentsResult.titleLeafList?.length || 0;

        // 如果有目录页，先从目录页收集目录标题
        if (templateSectionResult && templateContentsResult.titleLeafList?.length) {
            const titleName: string[] = [];

            // 不考虑第一页
            for (let index = 0; index < contentsNumber; index++) {
                const title = templateContentsResult.titleLeafList[index].text.replaceAll(' ', '');
                const firstSectionTitle = templateSectionResult.ctrTitleLeaf?.text?.replaceAll(' ', '');
                if (title && title !== firstSectionTitle) {
                    titleName.push(title);
                }
            }

            const {
                left = 0,
                top = 0,
                width = 0,
                height = 0,
            } = templateSectionResult.ctrTitleLeaf?.contentBlock.getRect() || {};
            // 接下来找和第一个章节页相似，且标题是收集到的目录标题的页
            for (let index = 4; index < templatePageTextList.length; index++) {
                // 理论上优化下，从5页开始找，前四页一般是封面、前言、目录、第一个章节页，后面的章节页不会出现太早
                const page = templatePageTextList[index];
                if (page) {
                    const possibleSection =  this.analyzerTemplateSection(page);
                    const title = possibleSection.ctrTitleLeaf?.text?.replaceAll(' ', '') || '';
                    if (titleName.includes(title)) {
                        const possibleRect = possibleSection.ctrTitleLeaf?.contentBlock.getRect();
                        if (possibleRect) {
                            if (
                                possibleRect.left > left * MIN_SCALE
                                && possibleRect.left < left * MAX_SCALE
                                && possibleRect.top > top * MIN_SCALE
                                && possibleRect.top < top * MAX_SCALE
                            ) {
                                subSimilarResult.push(possibleSection);
                            }
                        }
                    }
                }
            }
        }

        return subSimilarResult;
    }

    public analyzerContent(templatePageText: PageText) {
        const { texts = [] } = templatePageText;

        // 先按文字属性收集相似文本
        const verifiedContentsTitleMap = this.verifyContentByFontInfoAndIgnoreSomeText(texts);
        const autoTag: IAutoTag = {};

        let textLeafNodeList: TextLeafNode[] | undefined;
        let fontSize = 0;
        verifiedContentsTitleMap.forEach((value) => {
        });

        verifiedContentsTitleMap.forEach((value, textSize) => {
            if (textSize > fontSize) {
                fontSize = textSize;
                textLeafNodeList = value;
            }
        });

        if (textLeafNodeList?.length === 1) {
            const textLeaf = textLeafNodeList[0];
            autoTag.title = {
                text: textLeaf.text,
                model: textLeaf.contentBlock?.visualBlocks?.[0]?.info?.element,
            };

            verifiedContentsTitleMap.delete(fontSize);
        }
    }

    /**
     * 将同字体大小文字，按类别归类
     * 例如归类为序号、纯文本、百分比等信息
     * @param textLeafList
     */
    public verifyContentByTextType(textLeafList: Array<TextLeafNode>) {

    }

    /**
     * 是否是一个强标题
     * @param text
     */
    public isStrongTitle(text: string) {
        const parseText = text.replaceAll(' ', '');
        return parseText.includes('title') || parseText.includes('标题');
    }

    /**
     * 是否是一个强序号
     * @param text
     */
    public isStrongSerialNumber(text: string) {
        // const parseText = text.replaceAll(' ', '');
        // return parseText.includes('title') || parseText.includes('标题');
    }

    /**
     * 获取文档的自定义色/主题色数量，可用于是否有配套主题
     * 目前，如果50%以上的填充类颜色是主题色的话，我们就认为这个文档带有配套主题
     * @param presentation
     */
    public getPresentationColorInfo(presentation: IPresentationModel): IAnalyzerColorNumber {
        const result: IAnalyzerColorNumber = {
            customColorNumber: 0,
            themeColorNumber: 0,
        };

        presentation.children.forEach((slideModel) => {
            this.getElementListColorInfo(slideModel.children, result);
        });

        return result;
    }

    /**
     * 根据子元素列表获取颜色数据
     * @param elements
     * @param result
     */
    public getElementListColorInfo(elements: IElementBaseModel[], result: IAnalyzerColorNumber): IAnalyzerColorNumber {
        elements.forEach((element) => {
            if (
                element.type === UIModelType.SHAPE
                || element.type === UIModelType.CONNECTOR
            ) {
                this.getElementColorInfo(element, result);
            }

            if (element.type === UIModelType.GROUP) {
                this.getElementListColorInfo((element as IGroupModel).children, result);
            }
        });

        return result;
    }

    /**
     * 获取形状颜色信息
     * @param shapeModel
     * @param result
     */
    public getElementColorInfo(shapeModel: IElementBaseModel, result: IAnalyzerColorNumber): IAnalyzerColorNumber {
        const { props } = shapeModel;

        // 收集边框纯色填充信息
        const borderColorValue = shapeModel.getBorder().getBorderInfo()?.tdColor?.colorValue;
        this.setColorInfo(borderColorValue, result);

        // 收集背景纯色填充信息
        const solidFillColorValue = props.style.fillBackground?.solidFill?.color.colorValue;
        this.setColorInfo(solidFillColorValue, result);

        // 收集边框渐变填充信息
        const borderGradList = shapeModel.getBorder().getBorderInfo()?.gradFill?.gsList;
        borderGradList?.forEach((point) => {
            const pointColorValue = point.color?.colorValue;
            this.setColorInfo(pointColorValue, result);
        });

        // 收集背景渐变填充信息
        const gradList = props.style.fillBackground?.gradFill?.gsList;
        gradList?.forEach((point) => {
            const pointColorValue = point.color?.colorValue;
            this.setColorInfo(pointColorValue, result);
        });

        // 图案填充和图片填充可以不考虑
        return result;
    }

    /**
     *
     * @param borderColorValue
     * @param result
     * @private
     */
    public setColorInfo(borderColorValue: string | STSchemeColorVal | undefined, result: IAnalyzerColorNumber) {
        if (Number.isSafeInteger(borderColorValue)) {
            result.themeColorNumber = result.themeColorNumber + 1;
        } else if (borderColorValue) {
            result.customColorNumber = result.customColorNumber + 1;
        }
    }

    /**
     * 根据sdk的data拿模版分析数据
     * @param presentationData
     */
    public async analyzerNonStandardTemplateByPresentationModel(presentationData: IPresentationData) {
        const templateUiDataService = await this.createTemplateUiDataService(presentationData);
        return this.analyzerNonStandardTemplate({
            templatePresentation: templateUiDataService.presentation,
        });
    }

    /**
     * 根据模版id拿模版分析结果
     * @param templateId
     */
    public async analyzerNonStandardTemplateByTemplateId(templateId: string) {
        const presentationData = await this.initTemplateData(templateId);
        return this.analyzerNonStandardTemplateByPresentationModel(presentationData);
    }

    /**
     * 首次切换时拉取模版数据
     */
    public initTemplateData = async (templateId: string): Promise<IPresentationData> => {
        const slideFileData = await fetchTemplateByIdByNonVip(templateId);
        const presentationData = convert(cloneDeep(slideFileData));
        presentationData.noteSlideMap = {};
        return presentationData;
    };

    /**
     * 分析模版封面
     * @param coverPageText
     * @private
     */
    private analyzerTemplateCover(coverPageText: PageText): IVerifiedCoverTitle {
        const { texts = [] } = coverPageText;

        // 先按文字属性收集相似文本
        const verifiedContentsTitleMap = this.verifyContentByFontInfo(texts);
        return {
            ...this.verifyTemplateCoverType(verifiedContentsTitleMap),
            pageModel: coverPageText.pageModel,
        };
    }

    private analyzerTemplateSubCover(coverPageText: PageText): IVerifiedCoverTitle {
        const { texts = [] } = coverPageText;

        // 先按文字属性收集相似文本
        const verifiedContentsTitleMap = this.verifyContentByFontInfo(texts);
        return {
            ...this.verifyTemplateCoverType(verifiedContentsTitleMap),
            pageModel: coverPageText.pageModel,
        };
    }


    /**
     * 模版封面数据分类
     * @param contentMap
     */
    private verifyTemplateCoverType = (contentMap: Map<number, Array<TextLeafNode>>): IVerifiedCoverTitle => {
        let ctrTitleSize = 0;
        let ctrTitleLeaf: TextLeafNode | undefined;
        let ctrTitleLevel = 0;
        let subTitleLeaf: TextLeafNode | undefined;
        let indexLeaf: TextLeafNode | undefined;
        let subIndexLeaf: TextLeafNode | undefined;

        // 找主标题
        for (const [fontSize, leafNodeList] of contentMap) {
            if (fontSize > ctrTitleSize) {
                const newLeafNodeList = leafNodeList.filter(leaf => !this.isIgnoreText(leaf));

                const leaf = newLeafNodeList[0];
                if (leaf) {
                    ctrTitleSize = fontSize;
                    ctrTitleLeaf = leaf;
                    ctrTitleLevel = leaf.level;
                }
            }
        }

        // 找副标题
        for (const [fontSize, leafNodeList] of contentMap) {
            if (leafNodeList[0]?.level === ctrTitleLevel + 1) {
                if (!this.isIgnoreText(leafNodeList[0])) {
                    subTitleLeaf = leafNodeList[0];
                }
            }
        }

        // 找索引
        // @ts-ignore
        for (const [fontSize, leafNodeList] of contentMap) {
            // @ts-ignore
            const res = this.getIndexAndSubIndexInfo(leafNodeList, indexLeaf, subIndexLeaf);
            indexLeaf = res.indexLeaf;
            subIndexLeaf = res.subIndexLeaf;
        }

        return {
            ctrTitleLeaf,
            subTitleLeaf,
            indexLeaf,
            subIndexLeaf,
        };
    };

    /**
     * 收集索引/副索引信息
     * @param leafNodeList
     * @param indexLeaf
     * @param subIndexLeaf
     * @private
     */
    private getIndexAndSubIndexInfo(
        leafNodeList: Array<TextLeafNode>,
        indexLeaf: TextLeafNode | undefined,
        subIndexLeaf: TextLeafNode | undefined,
    ) {
        leafNodeList.forEach((leaf) => {
            if (this.isIgnoreText(leaf) && this.getIsFirst(leaf?.text)) {
                if (!indexLeaf) {
                    indexLeaf = leaf;
                } else if (!subIndexLeaf) {
                    subIndexLeaf = leaf;
                }
            }
        });
        return { indexLeaf, subIndexLeaf };
    }

    private verifyUserCoverType = (contentMap: Map<number, Array<TextLeafNode>>): IVerifiedCoverTitle => {
        let ctrTitleSize = 0;
        let ctrTitleLeaf: TextLeafNode | undefined;
        let ctrTitleLevel = 0;
        let subTitleLeaf: TextLeafNode | undefined;
        let indexLeaf: TextLeafNode | undefined;
        let subIndexLeaf: TextLeafNode | undefined;

        // 找主标题
        for (const [fontSize, leafNodeList] of contentMap) {
            if (fontSize > ctrTitleSize) {
                const newLeafNodeList = leafNodeList.filter(leaf => !this.isIgnoreText(leaf));

                const leaf = newLeafNodeList[0];
                if (leaf) {
                    ctrTitleSize = fontSize;
                    ctrTitleLeaf = leaf;
                    ctrTitleLevel = leaf.level;
                }
            }
        }

        // 找副标题
        for (const [fontSize, leafNodeList] of contentMap) {
            if (leafNodeList[0]?.level === ctrTitleLevel + 1) {
                if (!this.isIgnoreText(leafNodeList[0])) {
                    subTitleLeaf = leafNodeList[0];
                }
            }
        }

        // 找索引
        for (const [fontSize, leafNodeList] of contentMap) {
            // @ts-ignore
            const res = this.checkCoverTitle(leafNodeList, indexLeaf, subIndexLeaf);
            indexLeaf = res.indexLeaf;
            subIndexLeaf = res.subIndexLeaf;
        }

        return {
            ctrTitleLeaf,
            subTitleLeaf,
            indexLeaf,
            subIndexLeaf,
        };
    };

    /**
     * 检查标题文字是否合法
     * @param leafNodeList
     * @param indexLeaf
     * @param subIndexLeaf
     * @private
     */
    private checkCoverTitle(
        leafNodeList: Array<TextLeafNode>,
        indexLeaf: TextLeafNode | undefined,
        subIndexLeaf: TextLeafNode | undefined,
    ) {
        leafNodeList.forEach((leaf) => {
            if (this.isIgnoreText(leaf) && this.getIsFirst(leaf?.text)) {
                if (!indexLeaf) {
                    indexLeaf = leaf;
                } else if (!subIndexLeaf) {
                    subIndexLeaf = leaf;
                }
            }
        });
        return { indexLeaf, subIndexLeaf };
    }

    /**
     * 分析模版目录
     */
    private analyzerTemplateContents = (
        pageText: PageText,
        contentsNumber: TContentsNumberType,
    ): IVerifiedContentsTitle => {
        const { texts = [] } = pageText || {};

        // 先按文字属性收集相似文本
        const verifiedContentsTitleMap = this.verifyContentByFontInfo(texts);

        // 再分类到对应属性
        return {
            ...this.verifyTemplateContentsType(verifiedContentsTitleMap, contentsNumber),
            pageModel: pageText.pageModel,
        };
    };

    /**
     * 分类位置目录数量的目录页
     * @param pageText
     */
    private analyzerTemplateContentsUnKnow = (pageText: PageText): IVerifiedContentsTitle => {
        const { texts = [] } = pageText || {};

        // 先按文字属性收集相似文本
        const verifiedContentsTitleMap = this.verifyContentByFontInfo(texts);

        // 再分类到对应属性
        return {
            ...this.verifyUserContentsType(verifiedContentsTitleMap),
            pageModel: pageText.pageModel,
        };
    };

    /**
     * 分类目录
     * @param contentMap
     */
    private verifyUserContentsType = (contentMap: Map<number,
            Array<TextLeafNode>>): Omit<IVerifiedContentsTitle, 'pageModel'> => {
        let ctrTitleSize = 0;
        let ctrTitleLeaf: TextLeafNode | undefined;

        let titleSize = 0;
        let titleNumber = 0;
        let titleLeafList: TextLeafNode[] | undefined;

        let subTitleLeafList: TextLeafNode[] | undefined;

        // 找主标题
        for (const [fontSize, leafNodeList] of contentMap) {
            if (this.isIgnoreTextList(leafNodeList)) continue;
            if (leafNodeList.length === 1 && fontSize > ctrTitleSize) {
                ctrTitleSize = fontSize;
                ctrTitleLeaf = leafNodeList[0];
            }
        }

        contentMap.delete(ctrTitleSize);

        // 找目录项
        for (const [fontSize, leafNodeList] of contentMap) {
            // if (this.isIgnoreTextList(leafNodeList)) continue;
            let newLeafNodeList = leafNodeList.filter(leaf => !this.isIgnoreText(leaf));


            const modelMap = new Map<IElementBaseModel, TextLeafNode>();
            newLeafNodeList = newLeafNodeList.filter((leaf) => {
                const shapeModel = leaf.contentBlock?.visualBlocks?.[0]?.info?.element;
                if (!shapeModel) return false;
                if (this.isIgnoreText(leaf)) return;
                if (modelMap.has(shapeModel)) return false;
                modelMap.set(shapeModel, leaf);
                return true;
            });

            // 可以认为一个目录的目录数不能小于2
            if (newLeafNodeList.length < 2) continue;

            // 找对应目录数量，字体最大的那个文本，后续考虑如何增加权重判断
            if (modelMap.size > 0 && fontSize > titleSize) {
                titleNumber = newLeafNodeList.length;
                titleSize = fontSize;
                titleLeafList = newLeafNodeList;
            }
        }

        contentMap.delete(titleSize);

        // 找子目录项
        for (const [fontSize, leafNodeList] of contentMap) {
            if (this.isIgnoreTextList(leafNodeList)) continue;
            if (leafNodeList.length === titleNumber) {
                subTitleLeafList = leafNodeList;
            }
        }

        return {
            ctrTitleLeaf,
            titleLeafList,
            subTitleLeafList,
        };
    };

    /**
     * 模版目录数据分类
     */
    // eslint-disable-next-line complexity
    private verifyTemplateContentsType = (
        contentMap: Map<number,
            Array<TextLeafNode>>,
        contentsNumber: TContentsNumberType,
    ): Omit<IVerifiedContentsTitle, 'pageModel'> => {
        let ctrTitleSize = 0;
        let ctrTitleLeaf: TextLeafNode | undefined;

        let titleSize = 0;
        let titleNumber = 0;
        let titleLeafList: TextLeafNode[] | undefined;

        let subTitleLeafList: TextLeafNode[] | undefined;
        let indexLeaf: TextLeafNode | undefined;
        let subIndexLeaf: TextLeafNode | undefined;

        // 找主标题
        for (const [fontSize, leafNodeList] of contentMap) {
            if (this.isIgnoreTextList(leafNodeList)) continue;
            if (leafNodeList.length === 1 && fontSize > ctrTitleSize) {
                ctrTitleSize = fontSize;
                ctrTitleLeaf = leafNodeList[0];
            }
        }

        contentMap.delete(ctrTitleSize);

        // todo: 找副标题

        // 找目录项
        for (const [fontSize, leafNodeList] of contentMap) {
            // if (this.isIgnoreTextList(leafNodeList)) continue;
            let newLeafNodeList = leafNodeList.filter(leaf => !this.isIgnoreText(leaf));


            const modelMap = new Map<IElementBaseModel, TextLeafNode>();
            newLeafNodeList = newLeafNodeList.filter((leaf) => {
                const shapeModel = leaf.contentBlock?.visualBlocks?.[0]?.info?.element;
                if (!shapeModel) return false;
                if (modelMap.has(shapeModel)) return false;
                modelMap.set(shapeModel, leaf);
                return true;
            });

            // 找对应目录数量，字体最大的那个文本，后续考虑如何增加权重判断
            if (newLeafNodeList.length === contentsNumber) {
                if (fontSize > titleSize) {
                    titleNumber = newLeafNodeList.length;
                    titleSize = fontSize;
                    titleLeafList = newLeafNodeList;
                }
            }
        }

        contentMap.delete(titleSize);

        // 找子目录项
        for (const [fontSize, leafNodeList] of contentMap) {
            if (this.isIgnoreTextList(leafNodeList)) continue;
            if (leafNodeList.length === contentsNumber) {
                subTitleLeafList = leafNodeList;
            }
        }

        // todo: 分析目录项索引，后续做目录项扩展可能用到
        // for (const [fontSize, leafNodeList] of contentMap) {
        //     // @ts-ignore
        //     this.getIndexAndSubIndexInfo(leafNodeList, indexLeaf, subIndexLeaf);
        // }

        return {
            ctrTitleLeaf,
            titleLeafList,
            subTitleLeafList,
        };
    };

    /**
     * 分析模版封面
     * @param coverPageText
     * @private
     */
    private analyzerTemplateSection(coverPageText: PageText): IVerifiedCoverTitle {
        const { texts = [] } = coverPageText || {};

        // 先按文字属性收集相似文本
        const verifiedContentsTitleMap = this.verifyContentByFontInfo(texts);
        return {
            ...this.verifyTemplateCoverType(verifiedContentsTitleMap),
            pageModel: coverPageText.pageModel,
        };
    }

    /**
     * 分析模版封面
     * @param coverPageText
     * @private
     */
    private analyzerUserSection(coverPageText: PageText): IVerifiedCoverTitle {
        const { texts = [] } = coverPageText || {};

        // 先按文字属性收集相似文本
        const verifiedContentsTitleMap = this.verifyContentByFontInfo(texts);
        return {
            ...this.verifyUserCoverType(verifiedContentsTitleMap),
            pageModel: coverPageText.pageModel,
        };
    }

    /**
     * 分析模版封面
     * @param coverPageText
     * @private
     */
    private analyzerTemplateBackCover(coverPageText: PageText): IVerifiedCoverTitle {
        const { texts = [] } = coverPageText;

        // 先按文字属性收集相似文本
        const verifiedContentsTitleMap = this.verifyContentByFontInfo(texts);
        return {
            ...this.verifyTemplateCoverType(verifiedContentsTitleMap),
            pageModel: coverPageText.pageModel,
        };
    }


    /**
     * 根据文字信息分类内容
     * 暂时只有字号，后续要加上字色、字体
     * @param texts
     */
    private verifyContentByFontInfo = (
        texts: TextLeafNode[],
        deleteIgnoreText?: boolean,
    ): Map<number, Array<TextLeafNode>> => {
        const fontsMap = new Map<number, Array<TextLeafNode>>();
        texts.forEach((textLeaf) => {
            if (deleteIgnoreText && this.isIgnoreText(textLeaf)) {
                return;
            }

            const fontSize = textLeaf.contentBlock.visualBlocks[0]?.getFirstFontSize();
            const fontStyles = textLeaf.contentBlock.visualBlocks[0]?.getFirstFontStyle();
            if (fontSize) {
                let leftSet = fontsMap.get(fontSize);
                if (!leftSet) {
                    leftSet = [];
                    fontsMap.set(fontSize, leftSet);
                }

                leftSet.push(textLeaf);
            }
        });
        return fontsMap;
    };

    private verifyContentByFontInfoAndIgnoreSomeText = (texts: TextLeafNode[]): Map<number, Array<TextLeafNode>> => {
        const fontsMap = new Map<number, Array<TextLeafNode>>();
        texts.forEach((textLeaf) => {
            if (this.isIgnoreText(textLeaf) || this.isIgnorePureText(textLeaf)) {
                return;
            }

            const fontSize = textLeaf.contentBlock.visualBlocks[0]?.getFirstFontSize();
            const fontStyles = textLeaf.contentBlock.visualBlocks[0]?.getFirstFontStyle();
            if (fontSize) {
                let leftSet = fontsMap.get(fontSize);
                if (!leftSet) {
                    leftSet = [];
                    fontsMap.set(fontSize, leftSet);
                }

                leftSet.push(textLeaf);
            }
        });
        return fontsMap;
    };

    private isIgnoreTextList = (leafNodeList: Array<TextLeafNode>) => {
        let flag = false;
        leafNodeList.forEach((leaf) => {
            if (this.isIgnoreText(leaf)) {
                flag = true;
            }
        });

        return flag;
    };

    private isIgnorePureText = (leafNode: TextLeafNode) => {
        const reg = /^[.,。，、…\s]+$/;
        if (!leafNode?.text || reg.test(leafNode.text)) {
            return true;
        }

        return false;
    };

    /**
     * 是否是一个可以忽略纯英文/数字混合的字符串（且很短）
     * 排除一些关键字
     * @param leafNode
     */
    private isIgnoreText = (leafNode: TextLeafNode) => {
        let flag = false;

        if (leafNode?.text?.length && leafNode.text.length < 2) {
            return true;
        }

        const reg = /^[0-9a-zA-Z.,。，、\s]+$/;
        if (!leafNode?.text) {
            return true;
        }

        if (leafNode.text.toLowerCase().includes('contents')
            && leafNode.text.replaceAll(' ', '').includes('目录')
        ) {
            return true;
        }

        if (
            leafNode
            && reg.test(leafNode.text)
            && leafNode.text.length <= 10
            && !leafNode.text.toLowerCase().includes('thank')
            && !leafNode.text.includes('感谢')
            && !leafNode.text.includes('谢谢')
        ) {
            flag = true;
        }

        return flag;
    };

    /**
     * 格式化文本成为一个简洁文本，用于方便正则，比如去掉空格（e.g. 目   录 => 目录）
     * 后续在扩展
     * @param section
     * @private
     */
    private getPredicate(section: IVerifiedCoverTitle) {
        return (title) => {
            const lowerText = title.text.toLowerCase();
            return lowerText.replaceAll(' ', '') === (section.ctrTitleLeaf?.text)?.toLowerCase().replaceAll(' ', '');
        };
    }
}
